import React, { ComponentProps } from 'react';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { DefaultView, DeleteAction, OptionalEditView, Views } from '../../types/BlockWithViews';
import { useSonTranslation } from '../../translation/useSonTranslation';
import {
  BlockInteractions,
  Views as BlockInteractionsViews,
  DISPLAY_TYPE,
  createButtonObject,
} from '../../components/organisms/BlockInteractions';
import { Icons } from '../../components/atoms/SVG';
import { Namespace } from '../../translation/namespace';
import { isNotNullOrUndefined } from '../../lib/typeGuards';
import { PluginConfigurationDefaultView } from './views/PluginConfigurationDefaultView';
import { PluginConfigurationEditView } from './views/PluginConfigurationEditView';

type PluginConfigurationProps = Views<
  DefaultView<PluginConfigurationDefaultView> & OptionalEditView<PluginConfigurationEditView> & DeleteAction
>;

function PluginConfiguration({ views, activeView }: PluginConfigurationProps) {
  const { t } = useSonTranslation('PluginConfiguration');
  const { uuid, elementType } = useBlockContext();
  const { refreshCount } = useRefreshBlock();

  const buttons = [
    views.delete &&
    createButtonObject(
      BlockInteractionsViews.DELETE,
      Icons.ERASER,
      `${Namespace.PluginConfiguration}:erase`,
      DISPLAY_TYPE.MODAL,
    ),
    views.edit &&
    createButtonObject(
      BlockInteractionsViews.EDIT,
      Icons.EDIT_PENCIL,
      `${Namespace.PluginConfiguration}:edit`,
      DISPLAY_TYPE.COLLAPSIBLE,
      <PluginConfigurationEditView {...views.edit} />,
    ),
  ]
    .filter(isNotNullOrUndefined)
    .reduce((btn, elem) => ({ ...elem, ...btn }), {});

  return (
    <BlockInteractions
      key={refreshCount}
      headline={{
        text: t('headline'),
        visible: true,
        hierarchy: 3,
      }}
      alwaysShowInfo
      views={views}
      activeView={activeView}
      buttons={buttons}
      uuid={uuid}
      elementType={elementType}
    >
      <PluginConfigurationDefaultView {...views.default} />
    </BlockInteractions>
  );
}

export const PluginConfigurationBlock = asFetchableBlock(PluginConfiguration, BlockType.pluginconfiguration);
export type PluginConfigurationBlock = ComponentProps<typeof PluginConfigurationBlock>;
